//region :: Imports ::
import React, {ReactElement, ReactNode} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {MCancelIconName, MOkIconName} from '../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../Toolkit/Components/Controls/Dialog';
import MButton from '../Toolkit/Components/Controls/Button';
import MFormControl from '../Toolkit/Components/Controls/FormControl';
import {Enumeration} from '../Api/ControllerTypes/EnumerationTypes';
import MDropDown from '../Toolkit/Components/Controls/DropDown';
import {Drawing} from '../Api/ControllerTypes/DrawingTypes';
import MNumericBox from '../Components/Toolkit/NumericBox';
import {Divider} from 'primereact/divider';

//endregion

/** */
type Props = {
    drawnByValues: Enumeration[];
}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    newData: boolean;
    drawingData: Drawing;
    onUpdate?: (newData: boolean, data: Drawing) => void;
}

/** */
class DrawingDialog extends MComponent<Props, State> {
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();
        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.IsOKDisabled = this.IsOKDisabled.bind(this);
        this.ok_Click = this.ok_Click.bind(this);

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            newData: false,
            drawingData: {
                id: 0,
                parentId: '',
                drawingNum: '',
                drawnBy: 0,
                numOfDrawings: 0,
                paidPerPlan: 0,
                chargePerPlan: 0,
                createdBy: '',
                createdOn: new Date()
            }
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(newData: boolean, currentData: Drawing, inputCallback: (newData: boolean, data:Drawing) => void, position?: DialogPositionType) {
        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.newData = newData;
            draft.onUpdate = inputCallback;
            draft.drawingData = currentData;
        });
    }

    public hide() {
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private IsOKDisabled() {
        return this.state.drawingData.drawnBy === -1;
    }

    private ok_Click() {
        this.state.onUpdate!(this.state.newData, this.state.drawingData!);
        this.hide();
    }

    private getDialogTemplate(): ReactNode {
        const drawnByRows = this.props.drawnByValues.map(entry => ({
            value: entry.id,
            label: entry.name
        }));

        return (
            <div className="formgrid grid">
                <div className="col-12 md:col-6">
                    <MFormControl label={'Drawn By'} id={'drawnByField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown id={'drawnByValue'}
                                           disabled={false}
                                           items={drawnByRows}
                                           initialValue={this.state.drawingData?.drawnBy}
                                           onChange={e => this.safeSetState(draft => draft.drawingData.drawnBy = e!)}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                {this.state.newData && <div className="col-12 md:col-6">
                    <MFormControl label={'Num Of Drawings'} id={'NumOfDrawingsField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MNumericBox id={'numOfDrawingsValue'}
                                             maxLength={4}
                                             disabled={false}
                                             className={fieldClasses}
                                             decimalPlaces={2}
                                             useGrouping={false}
                                             onChange={e => this.safeSetState(draft => draft.drawingData.numOfDrawings = e)}
                                             initialValue={this.state.drawingData?.numOfDrawings}
                                />
                            );
                        }}
                    </MFormControl>
                </div>}

                <Divider/>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Paid per Plan'} id={'PaidPerPlanField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MNumericBox id={'paidPerPlanValue'}
                                             maxLength={7}
                                             disabled={false}
                                             className={fieldClasses}
                                             decimalPlaces={2}
                                             mode={'currency'}
                                             currency={'GBP'}
                                             useGrouping={false}
                                             onChange={e => this.safeSetState(draft => draft.drawingData.paidPerPlan = e)}
                                             initialValue={this.state.drawingData?.paidPerPlan}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-6">
                    <MFormControl label={'Charge per Plan'} id={'ChargePerPlanField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MNumericBox id={'chargePerPlanValue'}
                                             maxLength={7}
                                             disabled={false}
                                             className={fieldClasses}
                                             decimalPlaces={2}
                                             mode={'currency'}
                                             currency={'GBP'}
                                             useGrouping={false}
                                             onChange={e => this.safeSetState(draft => draft.drawingData.chargePerPlan = e)}
                                             initialValue={this.state.drawingData?.chargePerPlan}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
            </div>
        );
    }

    private getFooterTemplate(): ReactNode {
        return (
            <div>
                <MButton label="OK"
                         color="success"
                         icon={MOkIconName}
                         disabled={this.IsOKDisabled}
                         onClick={this.ok_Click}/>
                <MButton label={'Cancel'}
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.hide}/>
            </div>
        );
    }

    /** */
    public render(): ReactElement {

        return (
            <React.Fragment>
                <Dialog title={'Drawing Data'}
                        content={this.getDialogTemplate()}
                        icon={{name: 'pi pi-pencil', color: 'mcolor-info'}}
                        footer={this.getFooterTemplate()}
                        position={this.state.position}
                        canClose={false}
                        closeOnEscape={false}
                        visible={this.state.visible}
                        onHide={this.dialog_onHide}
                />
            </React.Fragment>

        );

    }

}

export default DrawingDialog;
